import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtCurrency, fmtDate, niceName, fmtAddress } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PHASE_TONE = { escriturado: "bg-green-50 text-green-800", sem_venda: "bg-slate-100 text-slate-600" };
const money = (v) => (v ? fmtCurrency(v) : "—");

function Row({ r, onOpen }) {
  return (
    <tr onClick={onOpen} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`pt-row-${r.ref}`}>
      <td className="py-3 px-3 font-mono text-xs text-ec-gold whitespace-nowrap">{r.ref}</td>
      <td className="py-3 px-3 text-ec-green max-w-[260px] truncate">{fmtAddress(r.address) || "—"}</td>
      <td className="py-3 px-3 text-slate-600 whitespace-nowrap">{niceName(r.consultant) || "—"}</td>
      <td className="py-3 px-3 whitespace-nowrap"><span className={`text-xs px-2 py-0.5 rounded-full ${PHASE_TONE[r.phase] || "bg-ec-green/10 text-ec-green"}`}>{r.phase_label}</span></td>
      <td className="py-3 px-3 text-right tabular-nums text-slate-600 whitespace-nowrap">{r.deed_date ? fmtDate(r.deed_date) : "—"}</td>
      <td className="py-3 px-3 text-right tabular-nums text-slate-700 whitespace-nowrap">{money(r.price)}</td>
      <td className="py-3 px-3 text-right tabular-nums whitespace-nowrap">
        <span className="font-semibold text-ec-green">{money(r.commission)}</span>
        {r.share_agency && <span className="block text-[11px] text-slate-400" data-testid={`pt-share-${r.ref}`}>líquida · partilha {r.share_pct ?? 0}% {r.share_agency}</span>}
      </td>
    </tr>
  );
}

export const ProcessTable = ({ query, onClose }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!query) { return; }
    setRows(null);
    api.get("/agency/processes", { params: query.params }).then(({ data }) => setRows(data.rows));
  }, [query]);
  const sum = (k) => (rows || []).reduce((s, r) => s + (r[k] || 0), 0);
  return (
    <Dialog open={!!query} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="process-table-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-ec-green" data-testid="process-table-title">{query?.title}</DialogTitle>
          <DialogDescription>Só de leitura. Comissão prevista líquida, depois de partilhas com outras agências.</DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto -mx-2">
          <table className="w-full min-w-[820px] text-sm" data-testid="process-table">
            <thead>
              <tr className="text-left text-xs text-slate-500">
                {["Referência", "Imóvel", "Consultor", "Fase"].map((h) => <th key={h} className="py-2 px-3 font-semibold">{h}</th>)}
                {["Escritura", "Preço", "Comissão prevista"].map((h) => <th key={h} className="py-2 px-3 font-semibold text-right">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {!rows ? <tr><td colSpan={7} className="py-10 text-center text-slate-400">A carregar…</td></tr>
                : !rows.length ? <tr><td colSpan={7} className="py-10 text-center text-slate-400" data-testid="process-table-empty">Sem processos.</td></tr>
                : rows.map((r) => <Row key={r.id} r={r} onOpen={() => navigate(`/processo/${r.id}`)} />)}
            </tbody>
            {rows?.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-semibold text-ec-green" data-testid="process-table-totals">
                  <td colSpan={5} className="py-3 px-3">{rows.length} processos</td>
                  <td className="py-3 px-3 text-right tabular-nums">{money(sum("price"))}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{money(sum("commission"))}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
